import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../config/Firebase";
import Header from "./Header";
import Footer from "./Footer";

// Carousel Component: Rotates images every 3 seconds
function Carousel({ images }) {
  const validImages = images.filter((img) => img);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!validImages.length) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % validImages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [validImages]);

  if (!validImages.length) {
    return <p className="text-gray-400">No images available</p>;
  }

  return (
    <div className="w-full h-64 relative overflow-hidden">
      {validImages.map((src, i) => (
        <img
          key={i}
          src={src}
          alt="Product showcase"
          loading="lazy"
          className={`absolute top-0 left-0 w-full h-full object-contain transition-opacity duration-500 ${
            i === currentIndex ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
    </div>
  );
}

export default function KFAProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const navigate = useNavigate();

  // Discount rate for all HalfFrame products
  const DISCOUNT_RATE = 10; // 10%

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const ref = collection(db, "Kids-Frame");
        const snap = await getDocs(ref);
        setProducts(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error("Error fetching products:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  if (loading) return <p className="text-center text-gray-600">Loading products...</p>;
  if (error)   return <p className="text-center text-red-500">Error: {error}</p>;
  if (!products.length)
    return <p className="text-center text-gray-500">No products found in "HalfFrame".</p>;

  const handleClick = (prod) => navigate("/product-details", { state: prod });

  return (
    <>
      <Header />

      <div className="container mx-auto px-4 py-8">
        {/* Title */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl Rubik-Bold text-black px-2">Kids Frame</h1>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {products.map((product) => {
            // calculate pricing
            const newCost = parseFloat(product.Cost) || 0;
            const oldCost = (newCost / (1 - DISCOUNT_RATE / 100)).toFixed(0);

            return (
              <div
                key={product.id}
                className="bg-white rounded-lg shadow-lg transition-transform transform hover:scale-105 cursor-pointer"
                onClick={() => handleClick(product)}
              >
                <Carousel
                  images={[
                    product.ImageUrl1,
                    product.ImageUrl2,
                    product.ImageUrl3,
                    product.ImageUrl4,
                  ]}
                />

                <div className="px-5 py-4">
                  {/* Price Section */}
                  <div className="Rubik-Bold text-xl text-gray-800 mb-2">
                    <span className="text-black text-2xl mr-2">
                      Price: ₹{newCost.toFixed(0)}
                    </span>
                    {newCost > 0 && (
                      <span className="text-gray-400 line-through mr-2 text-xl">
                        ₹{oldCost}
                      </span>
                    )}
                    {newCost > 0 && (
                      <span className="text-cyan-600 text-xl">
                        ({DISCOUNT_RATE}% OFF)
                      </span>
                    )}
                  </div>

                  {/* Name & PID */}
                  <div className="flex justify-between mb-1">
                    <p className="Rubik-Bold text-gray-800">{product.Name || "N/A"}</p>
                    <p className="text-gray-500 Rubik-Light">
                      PID: {product.ProductID || "N/A"}
                    </p>
                  </div>

                  {/* Frame Color */}
                  <p className="text-black Rubik-Light mb-1">
                    <span className="font-semibold">Frame Color:</span>{" "}
                    <span className="text-gray-600">{product.FrameColor || "Unknown"}</span>
                  </p>

                  {/* Material */}
                  <p className="text-black Rubik-Light mb-1">
                    <span className="font-semibold">Material:</span>{" "}
                    <span className="text-gray-600">{product.Material || "Unknown"}</span>
                  </p>

                  {/* Status */}
                  <p
                    className={`font-semibold flex items-center ${
                      product.Status === "Available"
                        ? "text-green-500"
                        : product.Status === "Sold"
                        ? "text-red-500"
                        : "text-gray-500"
                    }`}
                  >
                    {product.Status === "Available" && (
                      <span className="inline-flex items-center justify-center rounded-full bg-green-100 p-1 mr-1">
                        <svg
                          className="w-4 h-4 text-green-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </span>
                    )}
                    {product.Status || "Unknown"}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Footer />
    </>
  );
}